import { useGetList } from "ra-core";
import { useEffect, useState } from "react";
import { useRedirect } from "ra-core";
import {
  Package,
  FileText,
  Truck,
  MessageSquare,
  CreditCard,
  TrendingUp,
} from "lucide-react";

import type { Contact, ContactNote } from "../types";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardStepper } from "./DashboardStepper";
import { DealsChart } from "./DealsChart";
import { HotContacts } from "./HotContacts";
import { TasksList } from "./TasksList";
import { Welcome } from "./Welcome";

type KpiData = {
  active_orders: number;
  in_transit: number;
  open_quotations: number;
  unread_inquiries: number;
  pending_payment: number;
  total_revenue_usd: number;
};

const useKpis = () => {
  const [kpis, setKpis] = useState<KpiData | null>(null);

  useEffect(() => {
    const supabase = getSupabaseClient();
    Promise.all([
      supabase.from("orders").select("id, status, payment_status, total, currency", { count: "exact", head: false }),
      supabase.from("order_shipments").select("id, status", { count: "exact", head: false }),
      supabase.from("quotations").select("id, status", { count: "exact", head: false }),
      supabase.from("inquiries").select("id, status", { count: "exact", head: false }),
    ]).then(([orders, shipments, quotations, inquiries]) => {
      const orderRows = orders.data ?? [];
      const shipmentRows = shipments.data ?? [];
      const quotationRows = quotations.data ?? [];
      const inquiryRows = inquiries.data ?? [];

      const activeStatuses = ["confirmed", "processing", "shipped", "pending"];
      const active_orders = orderRows.filter((o) => activeStatuses.includes(o.status)).length;
      const in_transit = shipmentRows.filter((s) => s.status === "in_transit").length;
      const open_quotations = quotationRows.filter((q) => ["draft", "sent"].includes(q.status)).length;
      const unread_inquiries = inquiryRows.filter((i) => i.status === "unread").length;
      const pending_payment = orderRows.filter((o) => o.payment_status === "pending").length;
      const total_revenue_usd = orderRows
        .filter((o) => o.status === "delivered" && o.currency === "USD")
        .reduce((sum, o) => sum + (o.total ?? 0), 0);

      setKpis({ active_orders, in_transit, open_quotations, unread_inquiries, pending_payment, total_revenue_usd });
    });
  }, []);

  return kpis;
};

const KpiCard = ({
  icon: Icon,
  label,
  value,
  sub,
  color,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  sub?: string;
  color: string;
  onClick?: () => void;
}) => (
  <div
    className={`bg-card border rounded-lg p-4 flex items-center gap-4 ${onClick ? "cursor-pointer hover:bg-accent/40 transition-colors" : ""}`}
    onClick={onClick}
  >
    <div className={`rounded-lg p-2.5 shrink-0 ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="text-xs font-medium text-muted-foreground mt-1">{label}</div>
      {sub && <div className="text-xs text-muted-foreground/70 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const BusinessKpis = () => {
  const kpis = useKpis();
  const redirect = useRedirect();

  if (!kpis) return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-card border rounded-lg p-4 h-20 animate-pulse" />
      ))}
    </div>
  );

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      <KpiCard
        icon={Package}
        label="活跃订单"
        value={kpis.active_orders}
        color="bg-blue-100 text-blue-700"
        onClick={() => redirect("/orders")}
      />
      <KpiCard
        icon={Truck}
        label="运输中"
        value={kpis.in_transit}
        sub="货运跟踪"
        color="bg-orange-100 text-orange-700"
        onClick={() => redirect("/logistics")}
      />
      <KpiCard
        icon={FileText}
        label="待确认报价"
        value={kpis.open_quotations}
        color="bg-yellow-100 text-yellow-700"
        onClick={() => redirect("/quotations")}
      />
      <KpiCard
        icon={MessageSquare}
        label="未读询盘"
        value={kpis.unread_inquiries}
        color="bg-red-100 text-red-700"
        onClick={() => redirect("/inquiries")}
      />
      <KpiCard
        icon={CreditCard}
        label="待收款订单"
        value={kpis.pending_payment}
        color="bg-purple-100 text-purple-700"
        onClick={() => redirect("/orders")}
      />
      <KpiCard
        icon={TrendingUp}
        label="已收款 (USD)"
        value={kpis.total_revenue_usd > 0 ? `$${kpis.total_revenue_usd.toLocaleString()}` : "$0"}
        color="bg-green-100 text-green-700"
      />
    </div>
  );
};

export const Dashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });

  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  const { total: totalDeal, isPending: isPendingDeal } = useGetList<Contact>(
    "deals",
    {
      pagination: { page: 1, perPage: 1 },
    },
  );

  const isPending = isPendingContact || isPendingContactNotes || isPendingDeal;

  if (isPending) {
    return null;
  }

  if (!totalContact) {
    return <DashboardStepper step={1} />;
  }

  if (!totalContactNotes) {
    return <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  }

  return (
    <div className="mt-1">
      <BusinessKpis />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-3">
          <div className="flex flex-col gap-4">
            {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
            <HotContacts />
          </div>
        </div>
        <div className="md:col-span-6">
          <div className="flex flex-col gap-6">
            {totalDeal ? <DealsChart /> : null}
            <DashboardActivityLog />
          </div>
        </div>
        <div className="md:col-span-3">
          <TasksList />
        </div>
      </div>
    </div>
  );
};
